// features/workstation-layer2.js — LAYER 2 DRAWER (Lens + Depth split)
// ═══════════════════════════════════════════════════════════
// Slides in from the right when the [Excavate] button in the top
// bar is clicked. Two columns:
//   - LEFT  = Lens  — moment-level observations (lens_findings table)
//   - RIGHT = Depth — period-level reports (space_depth_reports table)
//
// Both columns reuse the existing data sources — no new queries, no
// new API routes. Just a different UI for the same content. When the
// user closes Layer 2, the drawer slides out and Layer 1 gets the
// screen back.
//
// Phase 2 scope:
//   - Fetch lens_findings for this workspace (reuses Space infra)
//   - Fetch space_depth_reports for this workspace (reuses Space infra)
//   - Split view, simple rendering (no chat inside for now — the
//     Lens chat feature lives in features/chat-workspace-synthesis.js
//     which we keep for Phase 3 when we wire it back in)
//   - Empty states for both sides
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { useVoice } from '../hooks/use-voice';
import { useArtifacts } from '../hooks/use-artifacts';
import MessageContent from '../components/message-content';
import GroundedAnswerCardV2 from '../components/grounded-answer-card-v2';
import ChatInputBar from '../components/chat-input-bar';
import IntelligenceFeature from './intelligence-feature';
import ReflectColumn from '../components/reflect-column';

// Lens finding type → palette + display label
// Covers the 18 finding types from the new lens detector + the
// legacy types (personal, group, note) for backward compat with
// findings created before the type taxonomy expanded.
const LENS_PALETTES = {
  // ANALYTICAL types
  pattern:           { color: '#c8a96e', label: 'Pattern' },
  convergence:       { color: '#c8a96e', label: 'Convergence' },
  causal_chain:      { color: '#c8a96e', label: 'Causal Chain' },
  gap:               { color: '#7a9e82', label: 'Gap' },
  contradiction:     { color: '#c46050', label: 'Contradiction' },
  hidden_assumption: { color: '#7a9e82', label: 'Hidden Assumption' },
  binding_constraint:{ color: '#7a9e82', label: 'Binding Constraint' },
  cross_domain:      { color: '#9b8fc4', label: 'Cross-Domain' },
  indicator:         { color: '#9b8fc4', label: 'Indicator' },
  structural_insight:{ color: '#9b8fc4', label: 'Structural Insight' },
  structural_trap:   { color: '#c46050', label: 'Structural Trap' },
  // PROGRESS types
  breakthrough:      { color: '#c8a96e', label: 'Breakthrough' },
  decision:          { color: '#c8a96e', label: 'Decision' },
  reframe:           { color: '#c8a96e', label: 'Reframe' },
  idea_worth_keeping:{ color: '#c8a96e', label: 'Idea Worth Keeping' },
  // FORWARD types
  key_question:      { color: '#7a9e82', label: 'Key Question' },
  next_experiment:   { color: '#7a9e82', label: 'Next Experiment' },
  // Legacy types (auto-generated by old detector + user notes)
  personal:          { color: '#c8a96e', label: 'Personal' },
  group:             { color: '#9b8fc4', label: 'Group' },
  note:              { color: '#7a9e82', label: 'Note' },
};

export default function WorkstationLayer2({ workspaceId, supabase, userId, userRecord, voiceConfig, createFileNode, visible, onClose, isMultiUser = false }) {
  const { saveArtifact } = useArtifacts({ userId, supabase });
  const [findings, setFindings] = useState([]);
  // ── Lens chat state ──
  // When non-null, the lens column swaps from "findings list" to
  // "chat thread" mode. The active finding is shown at the top as
  // context, lens_messages are loaded and rendered as bubbles, and
  // the user can send new messages via /api/space-session-lens-chat
  // (which streams Sonnet replies and persists both sides to
  // lens_messages — independent from the main messages table, no
  // intelligence pipelines fire).
  const [lensChatActive, setLensChatActive] = useState(null);
  const [lensChatMessages, setLensChatMessages] = useState([]);
  const [lensChatLoading, setLensChatLoading] = useState(false);
  const [lensChatInput, setLensChatInput] = useState('');
  const [lensChatStreaming, setLensChatStreaming] = useState(false);
  // Lens chat input controls — file, deep search, deep mode all
  // wired through to /api/space-session-lens-chat. These match the
  // layer 1 chat panel's controls 1:1 via the shared ChatInputBar
  // component (components/chat-input-bar.js).
  const [lensChatPendingFile, setLensChatPendingFile] = useState(null);
  const [lensChatDeepSearch, setLensChatDeepSearch] = useState(false);
  const [lensChatDeepMode, setLensChatDeepMode] = useState(false);
  const lensChatAbortRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  // Note input toggles — visual-only (deep search/mode arm but the
  // lens-note backend just saves a text note). Voice IS wired.
  const [lensDeepSearch, setLensDeepSearch] = useState(false);
  const [lensDeepMode, setLensDeepMode] = useState(false);
  // Single shared input ref — the ChatInputBar contentEditable lives
  // here. Only one of {note input, lens chat input} is on screen at a
  // time, so one ref is enough. Voice writes to it via innerText.
  const lensInputRef = useRef(null);
  // Tracks which surface voice should write to. Updated whenever
  // lensChatActive flips. useVoice closures read this ref so the
  // routing stays correct without re-creating the voice instance.
  const voiceTargetRef = useRef('note');
  useEffect(() => {
    voiceTargetRef.current = lensChatActive ? 'lensChat' : 'note';
  }, [lensChatActive]);

  // Generate Depth Report state
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // ── Voice integration on the lens input ──
  // useVoice writes transcribed text into the visible input. The
  // setInput closure branches via voiceTargetRef so the same voice
  // instance routes to either the note input or the lens chat input
  // depending on which is on screen.
  const voice = useVoice({
    session: voiceConfig?.session || null,
    userRecord,
    currentSessionId: workspaceId,
    threadHistory: voiceConfig?.threadHistory || [],
    voiceEnabled: voiceConfig?.voiceEnabled ?? true,
    inputRef: lensInputRef,
    setInput: (val) => {
      if (voiceTargetRef.current === 'lensChat') setLensChatInput(val);
      else setNoteText(val);
    },
    send: () => {
      if (voiceTargetRef.current === 'lensChat') sendLensChatMessage();
      else submitNote();
    },
    voiceSourceRef: useRef(false),
    voiceResponseCallbackRef: useRef(null),
  });
  const { voiceListening = false, toggleVoice = () => {} } = voice || {};

  // ── Generate a Depth Report manually ──
  // Calls /api/chat with action: 'spaceDepth' which routes to
  // handleSpaceDepth → generateSpaceDepthReport (no skip gate). The
  // returned report is prepended to local state immediately so the
  // user sees it without a refetch.
  async function generateDepthReport() {
    if (generatingReport || !workspaceId) return;
    setGeneratingReport(true);
    setReportError(null);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({
          action: 'spaceDepth',
          userId: userRecord?.id,
          spaceId: workspaceId,
          spaceType: 'space',
        }),
      });
      if (!res.ok) {
        setReportError('Could not generate report. Try again.');
        return;
      }
      const data = await res.json();
      if (data?.report) {
        // Build a row that matches the schema the renderer expects
        const newRow = {
          id: `local-${Date.now()}`,
          content: data.report,
          report_number: data.reportNumber || (reports.length + 1),
          message_count: data.messageCount || 0,
          period_start: data.periodStart || null,
          period_end: data.periodEnd || null,
          generated_at: new Date().toISOString(),
        };
        setReports(prev => [newRow, ...prev]);
        // Dispatch the new-item event so the Excavate button shows
        // a fresh badge if the user navigates away before reading
        try {
          window.dispatchEvent(new CustomEvent('workstation:new-excavate-item', {
            detail: { type: 'depth' },
          }));
        } catch (_) { /* non-fatal */ }
      } else if (data?.reason) {
        setReportError(data.reason);
      }
    } catch (e) {
      console.error('[workstation-layer2] depth report failed:', e.message);
      setReportError('Could not generate report. Try again.');
    } finally {
      setGeneratingReport(false);
    }
  }

  // ── Open lens chat for a specific finding ──
  // Switches the lens column from findings list to chat thread mode.
  // Loads existing lens_messages for the workspace from the
  // /api/space-session-lens-load endpoint (the same one the old
  // Space view used). Auto-focuses the input when ready.
  async function openLensChat(finding) {
    setLensChatActive(finding);
    setLensChatMessages([]);
    setLensChatInput('');
    setLensChatLoading(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`/api/space-session-lens-load?spaceId=${encodeURIComponent(workspaceId)}`, {
        headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLensChatMessages(data.messages || []);
      }
    } catch (e) {
      console.error('[workstation-layer2] lens chat load failed:', e.message);
    } finally {
      setLensChatLoading(false);
    }
  }

  function closeLensChat() {
    setLensChatActive(null);
    setLensChatMessages([]);
    setLensChatInput('');
  }

  // ── Send a lens chat message ──
  // Optimistically appends user message + streaming assistant placeholder,
  // then reads the SSE stream from /api/space-session-lens-chat. Tokens
  // append to the placeholder; when done event arrives, finalizes the
  // assistant message with its real id.
  // Now wired with file upload, deep mode (Opus), and deep search
  // (extended context) — all routed through the lens chat endpoint.
  async function sendLensChatMessage() {
    const text = lensChatInput.trim();
    if ((!text && !lensChatPendingFile) || lensChatStreaming) return;

    // Snapshot file + flags so user clears don't drop them mid-flight
    const fileToSend = lensChatPendingFile;
    const wasDeepSearch = lensChatDeepSearch;
    const wasDeepMode = lensChatDeepMode;
    setLensChatPendingFile(null);
    setLensChatDeepSearch(false);
    setLensChatDeepMode(false);

    // Build the messages payload for the endpoint (existing thread + new user msg)
    // For files, the marker stays out of the wire payload — the file
    // gets attached separately as fileData and the endpoint handles
    // the extraction via lib/file-extractors.
    const payload = [
      ...lensChatMessages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: text || ' ' },
    ];

    // Show the file marker in the optimistic user bubble so the user
    // sees their attachment immediately. The marker matches the chat
    // panel's format so MessageContent (if it ever renders this) can
    // show a thumbnail.
    let displayContent = text;
    if (fileToSend) {
      const marker = fileToSend.isImage ? `[img:${fileToSend.name}]` : `[file:${fileToSend.name}]`;
      displayContent = displayContent ? `${marker} ${displayContent}` : marker;
    }

    // Optimistic local state
    const tempUserId = `temp-u-${Date.now()}`;
    const tempAssistantId = `temp-a-${Date.now()}`;
    const userMsg = { id: tempUserId, role: 'user', content: displayContent, created_at: new Date().toISOString() };
    const assistantMsg = { id: tempAssistantId, role: 'assistant', content: '', streaming: true };
    setLensChatMessages(prev => [...prev, userMsg, assistantMsg]);
    setLensChatInput('');
    if (lensInputRef.current) lensInputRef.current.innerText = '';
    setLensChatStreaming(true);

    // Wire up an AbortController so the stop button can cancel mid-stream
    const abortController = new AbortController();
    lensChatAbortRef.current = abortController;

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/space-session-lens-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        signal: abortController.signal,
        body: JSON.stringify({
          spaceId: workspaceId,
          messages: payload,
          ...(fileToSend ? {
            fileData: {
              base64: fileToSend.base64,
              name: fileToSend.name,
              type: fileToSend.type,
              isImage: fileToSend.isImage,
              isPDF: fileToSend.isPDF,
            },
          } : {}),
          ...(wasDeepSearch ? { deepSearch: true } : {}),
          ...(wasDeepMode ? { deepMode: true } : {}),
        }),
      });

      if (!res.ok || !res.body) {
        // Replace streaming bubble with error text
        setLensChatMessages(prev => prev.map(m =>
          m.id === tempAssistantId
            ? { ...m, content: 'Sorry — Lens chat failed. Try again.', streaming: false }
            : m
        ));
        setLensChatStreaming(false);
        return;
      }

      // Read SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            if (data.t) {
              assistantContent += data.t;
              setLensChatMessages(prev => prev.map(m =>
                m.id === tempAssistantId ? { ...m, content: assistantContent } : m
              ));
            }
            if (data.done) {
              setLensChatMessages(prev => prev.map(m => {
                if (m.id === tempUserId && data.userMessageId) {
                  return { ...m, id: data.userMessageId };
                }
                if (m.id === tempAssistantId) {
                  return {
                    ...m,
                    id: data.assistantMessageId || m.id,
                    streaming: false,
                    content: data.text || assistantContent,
                  };
                }
                return m;
              }));
            }
          } catch (_) { /* skip malformed line */ }
        }
      }
    } catch (e) {
      // Aborts come through here too — leave whatever streamed in place,
      // just stop the streaming spinner.
      if (e.name !== 'AbortError') {
        console.error('[workstation-layer2] lens chat send failed:', e.message);
        setLensChatMessages(prev => prev.map(m =>
          m.id === tempAssistantId
            ? { ...m, content: 'Sorry — Lens chat failed. Try again.', streaming: false }
            : m
        ));
      } else {
        // Mark whatever streamed so far as final (no error text)
        setLensChatMessages(prev => prev.map(m =>
          m.id === tempAssistantId ? { ...m, streaming: false } : m
        ));
      }
    } finally {
      setLensChatStreaming(false);
      lensChatAbortRef.current = null;
    }
  }

  // Stop button handler — aborts the in-flight lens chat request.
  function abortLensChat() {
    if (lensChatAbortRef.current) {
      try { lensChatAbortRef.current.abort(); } catch (_) { /* already aborted */ }
    }
  }

  // ── File picker for lens chat input ──
  // Compresses images, sets pendingFile so the send button activates.
  // Same pattern as workstation-chat-panel.handleFilePickerSelect but
  // scoped to the lens chat input.
  function handleLensChatFileSelect({ base64, name, type }) {
    const isImage = type.startsWith('image/');
    const isPDF = type === 'application/pdf';
    const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

    if (isImage) {
      // Set pendingFile immediately so the send button appears
      setLensChatPendingFile({ base64: cleanBase64, name, type, isImage: true, isPDF: false });
      // Compress in background
      const img = new window.Image();
      img.onload = () => {
        const MAX = 1500;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setLensChatPendingFile({ base64: compressed.split(',')[1], name, type: 'image/jpeg', isImage: true, isPDF: false });
        if (lensInputRef.current) lensInputRef.current.focus();
      };
      img.onerror = () => {
        setLensChatPendingFile({ base64: cleanBase64, name, type, isImage: true, isPDF: false });
      };
      img.src = base64.includes(',') ? base64 : `data:${type};base64,${base64}`;
    } else {
      setLensChatPendingFile({ base64: cleanBase64, name, type, isImage, isPDF });
      if (lensInputRef.current) lensInputRef.current.focus();
    }
  }

  // ── Pin a lens finding to the canvas ──
  // Saves the finding text + reflection as a Document artifact in
  // the user's library, then creates a file node so the finding
  // appears as a tile in the canvas grid AND a node on the journey
  // map. Same pattern as pinDepthReport. The reflection is included
  // in the saved content because the user pinned the WHOLE thinking,
  // not just the headline.
  async function pinLensFinding(finding) {
    if (!finding || !finding.content) return false;
    try {
      const palette = LENS_PALETTES[finding.type] || LENS_PALETTES.group;
      const title = `${palette.label}: ${finding.content.slice(0, 80)}`;
      const body = [finding.content, finding.followup].filter(Boolean).join('\n\n');
      const saved = await saveArtifact(body, 'Document', workspaceId);
      if (saved) {
        await createFileNode?.({
          title: saved.title || title,
          artifactId: saved.id,
          fileSource: 'generated',
          chatMessageId: null,
        });
      }
      return true;
    } catch (e) {
      console.error('[workstation-layer2] pin lens finding failed:', e.message);
      return false;
    }
  }

  // ── Delete a lens finding ──
  // Calls /api/workspace/lens-delete which verifies ownership and
  // removes the row. Optimistically removes from local state.
  async function deleteLensFinding(findingId) {
    if (!findingId) return false;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/workspace/lens-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ findingId }),
      });
      if (res.ok) {
        setFindings(prev => prev.filter(f => f.id !== findingId));
        return true;
      }
      const err = await res.json().catch(() => ({}));
      console.error('[workstation-layer2] lens delete failed:', err.error || res.status);
      return false;
    } catch (e) {
      console.error('[workstation-layer2] lens delete failed:', e.message);
      return false;
    }
  }

  // ── Delete a depth report ──
  // Calls /api/workspace/depth-delete which verifies ownership and
  // removes the row. Optimistically removes from local state on
  // success. Confirmation is handled in the UI (two-click pattern).
  async function deleteDepthReport(reportId) {
    if (!reportId) return false;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/workspace/depth-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ reportId }),
      });
      if (res.ok) {
        setReports(prev => prev.filter(r => r.id !== reportId));
        return true;
      }
      const err = await res.json().catch(() => ({}));
      console.error('[workstation-layer2] depth delete failed:', err.error || res.status);
      return false;
    } catch (e) {
      console.error('[workstation-layer2] depth delete failed:', e.message);
      return false;
    }
  }

  // ── Pin a depth report to the canvas ──
  // Saves the report content as a Document artifact in the user's
  // library, then creates a file node so it appears as a tile in the
  // canvas grid AND a node on the journey map. The same pattern the
  // chat panel uses for AI-pinned messages.
  async function pinDepthReport(report) {
    if (!report || !report.content) return false;
    try {
      const title = `Depth Report #${report.report_number || ''}`.trim();
      const saved = await saveArtifact(report.content, 'Document', workspaceId);
      if (saved) {
        await createFileNode?.({
          title: saved.title || title,
          artifactId: saved.id,
          fileSource: 'generated',
          chatMessageId: null,
        });
      }
      return true;
    } catch (e) {
      console.error('[workstation-layer2] pin depth report failed:', e.message);
      return false;
    }
  }

  // ── Regenerate a whole depth report ──
  // For LEGACY reports (freeform single-text format from before the
  // multi-frame system), the per-frame Re-verify doesn't apply. Instead,
  // clicking Re-verify on the whole report triggers depth-cycle with
  // force=true which generates a fresh multi-frame report. We then
  // delete the old legacy report and add the new structured one to
  // local state — replacing the legacy with the new format.
  async function regenerateDepthReport(oldReportId) {
    if (!oldReportId) return false;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/workspace/depth-cycle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ workspaceId, force: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[workstation-layer2] regenerate failed:', err.error || res.status);
        return false;
      }
      const data = await res.json();
      if (!data?.cycled || !data?.report) {
        if (data?.reason) setReportError(data.reason);
        return false;
      }
      // Add the new report
      setReports(prev => [data.report, ...prev]);
      // Delete the old one
      try {
        await fetch('/api/workspace/depth-delete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession?.access_token || ''}`,
          },
          body: JSON.stringify({ reportId: oldReportId }),
        });
        setReports(prev => prev.filter(r => r.id !== oldReportId));
      } catch (e) {
        console.error('[workstation-layer2] cleanup of old report failed:', e.message);
      }
      return true;
    } catch (e) {
      console.error('[workstation-layer2] regenerate failed:', e.message);
      return false;
    }
  }

  // ── Re-verify a single depth frame ──
  // Calls /api/workspace/depth-frame-rerun with the workspace +
  // report + frame ids. Server fetches the current conversation,
  // re-runs that one frame's Sonnet prompt, updates the persisted
  // report's structured payload in place, and returns the new
  // analysis. We replace just the matching frame's analysis in
  // local state so the user sees the refresh immediately without
  // re-loading the whole report.
  async function rerunDepthFrame(reportId, frame) {
    if (!reportId || !frame?.frame_id) return false;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/workspace/depth-frame-rerun', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({
          workspaceId,
          reportId,
          frameId: frame.frame_id,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[workstation-layer2] rerun failed:', err.error || res.status);
        return false;
      }
      const data = await res.json();
      if (!data?.analysis) return false;
      // Update local state: find the report, parse its content, replace
      // just this frame's analysis, re-encode the content, replace the
      // report row in the reports list.
      setReports(prev => prev.map(r => {
        if (r.id !== reportId) return r;
        if (typeof r.content !== 'string' || !r.content.startsWith('SHIFT_FRAMES_V1:')) return r;
        try {
          const json = r.content.slice('SHIFT_FRAMES_V1:'.length);
          const parsed = JSON.parse(json);
          if (Array.isArray(parsed?.frames)) {
            const idx = parsed.frames.findIndex(f => f.frame_id === frame.frame_id);
            if (idx >= 0) {
              parsed.frames[idx] = {
                ...parsed.frames[idx],
                analysis: data.analysis,
                rerun_at: new Date().toISOString(),
              };
              return { ...r, content: `SHIFT_FRAMES_V1:${JSON.stringify(parsed)}` };
            }
          }
        } catch (_) { /* fall through */ }
        return r;
      }));
      return true;
    } catch (e) {
      console.error('[workstation-layer2] rerun failed:', e.message);
      return false;
    }
  }

  // ── Pin a single depth frame to the canvas ──
  // Saves just one frame's analysis (not the whole report) as a
  // Document artifact + file node. Useful when only one of the 1-2
  // frames in a weekly report is worth keeping. Frame body is wrapped
  // with a markdown header so the artifact reads as a self-contained
  // document.
  async function pinDepthFrame(frame) {
    if (!frame || !frame.analysis) return false;
    try {
      const title = `${frame.frame_name}: ${frame.analysis.slice(0, 60).replace(/[#\n]/g, ' ').trim()}`;
      const body = `# ${frame.frame_name}\n${frame.one_liner ? `*${frame.one_liner}*\n\n` : ''}${frame.analysis}`;
      const saved = await saveArtifact(body, 'Document', workspaceId);
      if (saved) {
        await createFileNode?.({
          title: saved.title || title,
          artifactId: saved.id,
          fileSource: 'generated',
          chatMessageId: null,
        });
      }
      return true;
    } catch (e) {
      console.error('[workstation-layer2] pin depth frame failed:', e.message);
      return false;
    }
  }

  // ── Submit a user note into the Lens column ──
  // Posts to /api/workspace/lens-note which inserts a personal-type
  // lens_finding via service role (lens_findings is RLS-locked).
  // Optimistically prepends to local state so the new note appears
  // immediately, then refreshes from the server response.
  async function submitNote() {
    const text = noteText.trim();
    if (!text || submittingNote) return;
    setSubmittingNote(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch('/api/workspace/lens-note', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ workspaceId, content: text }),
      });
      if (res.ok) {
        const { finding } = await res.json();
        if (finding) {
          setFindings(prev => [finding, ...prev]);
        }
        setNoteText('');
      } else {
        console.error('[workstation-layer2] note submit failed:', res.status);
      }
    } catch (e) {
      console.error('[workstation-layer2] note submit failed:', e.message);
    } finally {
      setSubmittingNote(false);
    }
  }

  useEffect(() => {
    if (!visible || !workspaceId || !supabase) return;
    let cancelled = false;

    // Loads findings + reports via the service-role API. Direct
    // supabase reads don't work because lens_findings has RLS without
    // policies and space_depth_reports has a broken email-based policy.
    // See pages/api/workspace/excavate-load.js for the why.
    async function loadAll() {
      setLoading(true);
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const res = await fetch(`/api/workspace/excavate-load?workspaceId=${encodeURIComponent(workspaceId)}`, {
          headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          console.error('[workstation-layer2] load failed:', res.status);
          return;
        }
        const data = await res.json();
        setFindings(data.findings || []);
        setReports(data.reports || []);
      } catch (e) {
        console.error('[workstation-layer2] load failed:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();
    return () => { cancelled = true; };
  }, [visible, workspaceId, supabase]);

  if (!visible) return null;

  // Layout splits on isMultiUser:
  //   Solo        → 62% drawer sliding in from the right, single
  //                 "Intelligence" header (legacy behavior).
  //   Multi-user  → full-screen overlay covering the workstation
  //                 interior up to the collapsed sidebar, 50/50
  //                 split with REFLECT | INTELLIGENCE tab headers.
  if (isMultiUser) {
    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: '#07070a',
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Tab header bar — REFLECT | INTELLIGENCE — split 50/50 with
            a single Close button on the far right. Each tab label
            sits over its own column so the identity is unambiguous. */}
        <div
          style={{
            height: 38,
            borderBottom: '1px solid #1e1e2a',
            background: '#07070a',
            display: 'flex',
            alignItems: 'stretch',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px',
            borderRight: '1px solid #1e1e2a',
            gap: 12,
            minWidth: 0,
          }}>
            <span style={{
              fontSize: 10,
              color: '#9b8fc4',
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              Reflect
            </span>
            <span style={{
              fontSize: 10,
              color: '#9b8fc4',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              opacity: 0.7,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              private space with {userRecord?.ai_name || 'Shift'} — only you can see this
            </span>
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px',
          }}>
            <span style={{
              fontSize: 10,
              color: '#c8a96e',
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}>
              Intelligence
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: '1px solid #2a2a2a',
                borderRadius: 4,
                color: '#666',
                fontSize: 10,
                fontFamily: 'Georgia, serif',
                padding: '4px 10px',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(196,96,80,0.4)'; e.currentTarget.style.color = '#c46050'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#666'; }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Body — equal columns, divider between */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #1e1e2a' }}>
            <ReflectColumn
              workspaceId={workspaceId}
              supabase={supabase}
              userId={userId}
              userRecord={userRecord}
              voiceConfig={voiceConfig}
              workspaceSession={null /* otherNames not needed for private 1:1 */}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <IntelligenceFeature
              userId={userId}
              supabase={supabase}
              userRecord={userRecord}
              workspaceId={workspaceId}
              onDiscuss={(report) => {
                onClose?.();
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('shift:submit-message', {
                    detail: { text: `Let's discuss this finding: "${report.title}" — ${report.body.slice(0, 200)}...` },
                  }));
                }, 300);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── SOLO LAYOUT (legacy single-column drawer) ──
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '62%',
        minWidth: 600,
        background: '#07070a',
        borderLeft: '1px solid #1e1e2a',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.4)',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
      }}
    >
      {/* Header */}
      <div
        style={{
          height: 38,
          borderBottom: '1px solid #1e1e2a',
          background: '#07070a',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: '#9b8fc4',
            fontFamily: 'Georgia, serif',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Intelligence
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid #2a2a2a',
            borderRadius: 4,
            color: '#666',
            fontSize: 10,
            fontFamily: 'Georgia, serif',
            padding: '4px 10px',
            cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(196,96,80,0.4)'; e.currentTarget.style.color = '#c46050'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#666'; }}
        >
          Close
        </button>
      </div>

      {/* Solo body — single Intelligence column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <IntelligenceFeature
          userId={userId}
          supabase={supabase}
          userRecord={userRecord}
          workspaceId={workspaceId}
          onDiscuss={(report) => {
            onClose?.();
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('shift:submit-message', {
                detail: { text: `Let's discuss this finding: "${report.title}" — ${report.body.slice(0, 200)}...` },
              }));
            }, 300);
          }}
        />
      </div>
    </div>
  );
}

// ── Depth report card ──────────────────────────────────────
// Renders a depth report with proper visual hierarchy. Two layouts
// based on the content shape:
//   1. SHIFT_FRAMES_V1:{...} → multi-frame structured report.
//      Renders one labeled card per analytical frame.
//   2. Anything else → legacy freeform report (backward compat).
//      Renders the markdown body via MessageContent.
// Both layouts share the same export footer (pin / copy / download).
//
// Exported so /pages/preview/depth-card.js can render it with mock
// data without spinning up the full Excavate drawer or waiting for
// real conversation data to accumulate.
export function DepthReportCard({ report, onPin, onPinFrame, onRerunFrame, onRegenerate, onDelete }) {
  // Delete is fully owned by the inner v2 card now (two-click
  // confirmation lives in GroundedAnswerCardV2). The parent just
  // forwards an onDelete callback that fires after the v2 card's
  // confirmation completes. No state needed up here.
  const periodLabel = formatPeriod(report.period_start, report.period_end);
  const handleDelete = onDelete ? () => onDelete(report.id) : undefined;

  // Try to decode as a structured multi-frame report. If the prefix
  // is present, render frame cards. Otherwise fall back to the legacy
  // freeform render via MessageContent.
  const FRAMES_PREFIX = 'SHIFT_FRAMES_V1:';
  const isStructured = typeof report.content === 'string' && report.content.startsWith(FRAMES_PREFIX);
  let frames = null;
  if (isStructured) {
    try {
      const json = report.content.slice(FRAMES_PREFIX.length);
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed?.frames)) frames = parsed.frames;
    } catch (_) { /* fall through to freeform */ }
  }

  // Quiet attribution line above the v2 cards. No delete button here
  // anymore — delete lives on the inner v2 card's footer next to Pin
  // (multi-frame: only the first frame shows it, so there's exactly
  // one delete button per report).
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        padding: '0 2px',
      }}>
        <span style={{
          fontSize: 9,
          color: '#666',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          Depth Report #{report.report_number || '—'}
        </span>
        <span style={{ color: '#2a2a2a' }}>·</span>
        <span style={{
          fontSize: 9,
          color: '#555',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          {report.message_count || 0} messages
        </span>
        {periodLabel && (
          <>
            <span style={{ color: '#2a2a2a' }}>·</span>
            <span style={{
              fontSize: 9,
              color: '#555',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
            }}>
              {periodLabel}
            </span>
          </>
        )}
      </div>

      {/* Body — multi-frame structured (preferred) or legacy freeform.
          Each frame is its own v2 card. The cards stand on their own;
          no parent border, no header strip. The delete button rides
          on the FIRST frame's footer (or the legacy card's footer)
          so there's exactly one delete affordance per report and it
          sits right next to Pin. */}
      {frames ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {frames.map((f, i) => (
            <DepthFrameV2Card
              key={f.frame_id || i}
              frame={f}
              reportId={report.id}
              onPin={onPinFrame}
              onRerun={onRerunFrame}
              onDelete={i === 0 ? handleDelete : undefined}
              showDelete={i === 0 && !!handleDelete}
            />
          ))}
        </div>
      ) : (
        <LegacyDepthReportV2Card
          report={report}
          onRegenerate={onRegenerate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

// ── Depth frame v2 card wrapper ────────────────────────────
// Maps a single frame from a depth report → GroundedAnswerCardV2.
// Header: frame name (e.g. "FIRST PRINCIPLES") + frame.one_liner
// as a sage italic subtitle. Gold border (depth analyses are
// always "deep" by definition). Body: frame.analysis as markdown.
// Action footer: Pin / Copy / Continue / Re-run on this frame.
function DepthFrameV2Card({ frame, reportId, onPin, onRerun, onDelete, showDelete = false }) {
  const [pinned, setPinned] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const payload = {
    header_label: frame.frame_name,
    header_color: '#c8a96e',
    header_subtitle: frame.one_liner,
    markdown_body: frame.analysis || '',
  };
  async function handlePin() {
    if (!onPin || pinned) return;
    const ok = await onPin(frame);
    if (ok) {
      setPinned(true);
      setTimeout(() => setPinned(false), 2400);
    }
  }
  async function handleRerun() {
    if (!onRerun || !reportId || rerunning) return;
    setRerunning(true);
    try {
      await onRerun(reportId, frame);
      // Parent updates local state with the new analysis. The frame
      // prop will be replaced on the next render with the fresh data.
    } finally {
      setRerunning(false);
    }
  }
  return (
    <GroundedAnswerCardV2
      payload={payload}
      pinned={pinned}
      showReverify={true}
      showChallenge={false}
      showContinue={true}
      showExport={false}
      showDelete={showDelete}
      onPin={handlePin}
      onCopy={() => {
        try {
          const text = `# ${frame.frame_name}\n${frame.one_liner ? `*${frame.one_liner}*\n\n` : ''}${frame.analysis || ''}`;
          navigator.clipboard.writeText(text);
        } catch (_) { /* clipboard may be blocked */ }
      }}
      onReverify={handleRerun}
      onContinue={() => { /* TODO A4: wire to start a thread on this frame */ }}
      onDelete={onDelete}
    />
  );
}

// Format the period_start / period_end timestamps as a compact range.
function formatPeriod(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const fmt = (d, includeMonth) => {
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    return includeMonth ? `${month} ${day}` : `${day}`;
  };
  if (sameMonth) {
    return `${fmt(start, true)}–${fmt(end, false)}`;
  }
  return `${fmt(start, true)} – ${fmt(end, true)}`;
}

// ── Lens finding v2 card wrapper ───────────────────────────
// Maps a lens_findings row → GroundedAnswerCardV2 payload.
// Header: type label + type color (one of 18 types or legacy)
// Body: finding text + followup as markdown
// Action footer: Pin / Copy / Continue thread / Delete
//   - Continue thread (MVP): dispatches workstation:continue-from-finding
//     event which the chat panel listens for; pre-fills the chat input
//     with "Tell me more about: [finding]" and closes the drawer.
//     The full lens chat thread (separate /api/space-session-lens-chat
//     surface with persisted lens_messages) stays deferred — see
//     DEFERRED_WORK.md "Card actions Lens".
//   - Delete: 2-click confirm, calls /api/workspace/lens-delete via
//     the parent's onDelete handler.
function LensFindingV2Card({ finding, onPin, onDelete, onClose, onContinueChat }) {
  const palette = LENS_PALETTES[finding.type] || LENS_PALETTES.group;
  const body = [finding.content, finding.followup].filter(Boolean).join('\n\n');
  const payload = {
    header_label: palette.label,
    header_color: palette.color,
    markdown_body: body,
    timestamp: formatRelativeTime(finding.created_at),
  };
  const [pinned, setPinned] = useState(false);
  async function handlePin() {
    if (!onPin || pinned) return;
    const ok = await onPin(finding);
    if (ok) {
      setPinned(true);
      // Reset after a few seconds so user can pin again if they want
      setTimeout(() => setPinned(false), 2400);
    }
  }
  return (
    <GroundedAnswerCardV2
      payload={payload}
      pinned={pinned}
      showReverify={false}
      showChallenge={false}
      showContinue={true}
      showExport={false}
      showDelete={true}
      onPin={handlePin}
      onCopy={() => {
        try {
          const text = [finding.content, finding.followup].filter(Boolean).join('\n\n');
          navigator.clipboard.writeText(text);
        } catch (_) { /* clipboard may be blocked */ }
      }}
      onContinue={() => {
        // Open the lens chat thread for this finding inside the Lens
        // column. The findings list is replaced by a chat thread; the
        // user can have a conversation with Shift about the finding,
        // persisted to lens_messages, no portrait/signal pipelines
        // fire (quiet surface).
        if (onContinueChat) onContinueChat(finding);
      }}
      onDelete={() => onDelete?.(finding.id)}
    />
  );
}

// Compact relative-time formatter for the v2 card timestamp slot
function formatRelativeTime(iso) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWk = Math.floor(diffDay / 7);
  return `${diffWk}w ago`;
}

// ── Legacy depth report v2 card wrapper ────────────────────
// Wraps a legacy freeform depth report (no SHIFT_FRAMES_V1: prefix)
// in the v2 card so it has the same action footer as multi-frame
// reports. The Re-verify button regenerates the WHOLE report by
// calling depth-cycle force=true and replacing the legacy row with
// a new multi-frame report. After this happens once, the report
// renders through the multi-frame path on next view.
function LegacyDepthReportV2Card({ report, onRegenerate, onDelete }) {
  const [regenerating, setRegenerating] = useState(false);
  const payload = {
    header_label: 'Depth Report',
    header_color: '#c8a96e',
    header_subtitle: 'Legacy report — re-verify to refresh into the new multi-frame format',
    markdown_body: report.content || '',
  };
  async function handleReverify() {
    if (!onRegenerate || regenerating) return;
    setRegenerating(true);
    try {
      await onRegenerate(report.id);
      // Parent removes the old report and adds the new one to local
      // state. This component will unmount as part of that update.
    } finally {
      setRegenerating(false);
    }
  }
  return (
    <GroundedAnswerCardV2
      payload={payload}
      showReverify={true}
      showChallenge={false}
      showContinue={false}
      showExport={false}
      showDelete={typeof onDelete === 'function'}
      onReverify={handleReverify}
      onPin={() => { /* legacy report pin handled by the parent header buttons */ }}
      onCopy={() => {
        try {
          navigator.clipboard.writeText(report.content || '');
        } catch (_) { /* clipboard may be blocked */ }
      }}
      onDelete={onDelete}
    />
  );
}

// ── Lens chat view ─────────────────────────────────────────
// Replaces the findings list when the user clicks "Continue thread"
// on a finding. Shows the active finding as a small reference card
// at the top, then a scrolling thread of lens_messages, then an
// input bar at the bottom. Submitting calls /api/space-session-lens-chat
// which streams a Sonnet response and persists both sides to
// lens_messages (separate from main messages, no intelligence
// pipelines fire — quiet surface).
function LensChatView({ activeFinding, messages, loading }) {
  const palette = LENS_PALETTES[activeFinding?.type] || LENS_PALETTES.group;

  return (
    <>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px 8px 18px', minHeight: 0 }}>
        {/* Active finding reference card */}
        {activeFinding && (
          <div style={{
            marginBottom: 14,
            padding: '10px 12px',
            background: `rgba(${palette.color === '#c8a96e' ? '200,169,110' : palette.color === '#7a9e82' ? '122,158,130' : palette.color === '#9b8fc4' ? '155,143,196' : '196,96,80'},0.06)`,
            border: `1px solid ${palette.color}33`,
            borderLeft: `2px solid ${palette.color}`,
            borderRadius: '0 6px 6px 0',
          }}>
            <div style={{
              fontSize: 8,
              color: palette.color,
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              About this {palette.label.toLowerCase()}
            </div>
            <div style={{
              fontSize: 11,
              color: '#a8a8a8',
              fontFamily: 'Georgia, serif',
              lineHeight: 1.55,
            }}>
              {activeFinding.content}
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ color: '#555', fontSize: 11, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 20 }}>
            Loading thread…
          </div>
        )}

        {/* Empty state */}
        {!loading && messages.length === 0 && (
          <div style={{
            color: '#555',
            fontSize: 11,
            fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
            textAlign: 'center',
            marginTop: 30,
            lineHeight: 1.6,
            padding: '0 12px',
          }}>
            Ask Shift to go deeper on this observation. Lens chat is private — it doesn't update your portrait or fire any pipelines.
          </div>
        )}

        {/* Message thread */}
        {!loading && messages.map((m) => (
          <div
            key={m.id}
            style={{
              marginBottom: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div style={{
              fontSize: 8,
              color: m.role === 'user' ? '#888' : '#9b8fc4',
              fontFamily: 'Georgia, serif',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: 3,
            }}>
              {m.role === 'user' ? 'You' : 'Shift'}
            </div>
            <div style={{
              maxWidth: '90%',
              fontSize: 12,
              lineHeight: 1.65,
              color: '#d4d4d4',
              fontFamily: 'Georgia, serif',
              background: m.role === 'user' ? '#111118' : 'none',
              border: m.role === 'user' ? '1px solid #252018' : 'none',
              borderRadius: m.role === 'user' ? '10px 10px 2px 10px' : 0,
              padding: m.role === 'user' ? '8px 12px' : '0',
              whiteSpace: 'pre-wrap',
            }}>
              {m.content}
              {m.streaming && <span style={{ color: '#9b8fc4', marginLeft: 4 }}>▮</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Input bar lives in the parent SpaceMode now (rendered as
          <ChatInputBar /> below the LensChatView). This keeps a single
          input bar instance per lens column and lets the parent route
          file/voice/deep-mode props through it consistently. */}
    </>
  );
}

// ── Generate Depth Report button ───────────────────────────
// Sage soft-overlay style with three-state hover. Used in two places:
// the empty state center (full size, with "+" icon) and at the top of
// a non-empty report list (compact, no icon, just the label).
function GenerateDepthButton({ onClick, loading, compact = false }) {
  const [hovered, setHovered] = useState(false);
  const bg = loading
    ? 'rgba(122,158,130,0.18)'
    : hovered
      ? 'rgba(122,158,130,0.2)'
      : 'rgba(122,158,130,0.1)';
  return (
    <button
      onClick={onClick}
      disabled={loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Generate a new depth report from the current workspace conversation"
      style={{
        background: bg,
        border: '1px solid rgba(122,158,130,0.4)',
        borderRadius: 6,
        color: '#7a9e82',
        fontSize: compact ? 9 : 11,
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: compact ? '5px 12px' : '8px 18px',
        cursor: loading ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 6 : 8,
        transition: 'background 0.15s',
      }}
    >
      <svg width={compact ? 9 : 11} height={compact ? 9 : 11} viewBox="0 0 16 16" fill="none">
        <path d="M8 2v12M2 8h12" stroke="#7a9e82" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {loading ? 'Generating…' : 'Generate Depth Report'}
    </button>
  );
}

